import type { UptimeDayPoint } from "../lib/api";

/** 90 (or fewer) daily-uptime squares for a single component.
 *
 * - Squares are rendered left-to-right oldest-first.
 * - Missing days (before the component started rolling up) render as
 *   a subtle gray placeholder so the row length is stable.
 * - Today's bar (last in the array) has a subtle pulsing border.
 */
export function UptimeBars({
  days,
  expectedLength = 90,
}: {
  days: UptimeDayPoint[];
  expectedLength?: number;
}) {
  // Pad on the left with placeholders so bars always end "today".
  const missing = Math.max(0, expectedLength - days.length);
  const placeholders = Array.from({ length: missing }, (_, i) => ({
    key: `placeholder-${i}`,
  }));
  // Report the window the average actually covers, not the window we
  // would like to show. A component added today has no track record, and
  // a status page that fills that gap with "100.00% over 90 days" is
  // stating something it does not know. With no rows we say so; with
  // partial rows we name the real day count.
  const avg =
    days.length === 0
      ? null
      : days.reduce((sum, d) => sum + d.uptime_percent, 0) / days.length;
  const headline =
    avg === null
      ? "No uptime history yet"
      : `${avg.toFixed(2)}% uptime over ${days.length} ${
          days.length === 1 ? "day" : "days"
        }`;

  return (
    <div
      className="mt-2"
      aria-label={`Uptime history over the last ${expectedLength} days`}
    >
      <div className="flex items-baseline justify-between">
        <p className="text-xs text-gray-500">{headline}</p>
        <p className="text-[10px] uppercase tracking-wider text-gray-400">
          {expectedLength}d ago &rarr; today
        </p>
      </div>
      <div className="mt-1 flex gap-[2px]" role="list">
        {placeholders.map((p) => (
          <span
            key={p.key}
            role="listitem"
            aria-label="no data"
            className="h-6 w-[3px] flex-none rounded-sm bg-gray-100"
          />
        ))}
        {days.map((d, i) => {
          const isToday = i === days.length - 1;
          const cls = classForDay(d);
          const tip = tooltip(d);
          return (
            <span
              key={d.date}
              role="listitem"
              title={tip}
              aria-label={tip}
              className={[
                "h-6 w-[3px] flex-none rounded-sm",
                cls,
                isToday ? "ring-1 ring-primary-400" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          );
        })}
      </div>
    </div>
  );
}

function classForDay(d: UptimeDayPoint): string {
  if (d.worst_state === "maintenance") return "bg-indigo-400";
  if (d.worst_state === "major_outage") return "bg-red-500";
  if (d.worst_state === "partial_outage") return "bg-red-400";
  if (d.worst_state === "degraded") return "bg-amber-400";
  // operational - use the brand emerald
  return "bg-primary-500";
}

function tooltip(d: UptimeDayPoint): string {
  const pct = d.uptime_percent.toFixed(2);
  const state = d.worst_state.replace("_", " ");
  const inc =
    d.incident_ids.length > 0 ? ` - ${d.incident_ids.length} incident(s)` : "";
  return `${d.date}: ${pct}% (${state})${inc}`;
}
